import React from "react";
import { FUNNEL_COLORS } from "@/lib/chart";
import { formatCount, formatPercent } from "@/lib/format";
import { ChevronRight } from "lucide-react";

function pct(a, b) { return b ? a / b : 0; }

/**
 * Horizontal marketing funnel: Leads -> Site Visits -> Bookings.
 * Each stage shows actual vs target and a bar proportional to the top stage,
 * with conversion chips between stages.
 */
export function FunnelStageBar({ leads, visits, bookings, layout = "vertical" }) {
  const stages = [
    { key: "leads", label: "Leads", actual: leads?.actual || 0, target: leads?.target || 0, color: FUNNEL_COLORS.leads },
    { key: "visits", label: "Site Visits", actual: visits?.actual || 0, target: visits?.target || 0, color: FUNNEL_COLORS.visits },
    { key: "bookings", label: "Bookings", actual: bookings?.actual || 0, target: bookings?.target || 0, color: FUNNEL_COLORS.bookings },
  ];
  const top = stages[0].actual || 1;
  const l2sv = pct(visits?.actual || 0, leads?.actual || 0);
  const sv2b = pct(bookings?.actual || 0, visits?.actual || 0);

  return (
    <div className="space-y-3" data-testid="funnel-visualization">
      {stages.map((s, i) => {
        const width = Math.max(6, Math.round((s.actual / top) * 100));
        const tgtPct = s.target ? Math.min(100, Math.round((s.actual / s.target) * 100)) : null;
        return (
          <div key={s.key}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium text-foreground">{s.label}</span>
              <span className="tabular-nums text-muted-foreground">
                <span className="font-semibold text-foreground">{formatCount(s.actual)}</span>
                {s.target ? <> / {formatCount(s.target)} tgt</> : null}
                {tgtPct != null ? <span className="ml-1">({tgtPct}%)</span> : null}
              </span>
            </div>
            <div className="h-7 w-full overflow-hidden rounded-md bg-[hsl(var(--surface-3))]">
              <div className="h-full rounded-md transition-[width] duration-300" style={{ width: `${width}%`, background: s.color }} />
            </div>
            {i < stages.length - 1 && (
              <div className="my-1 flex items-center gap-1 pl-1 text-[11px] text-muted-foreground">
                <ChevronRight className="h-3 w-3" />
                <span>{i === 0 ? "Lead\u2192Visit" : "Visit\u2192Booking"} conversion: <b className="text-foreground">{formatPercent(i === 0 ? l2sv : sv2b, 1)}</b></span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
